import EventType from 'ol/events/EventType';
import { CLASS_CONTROL, CLASS_UNSELECTABLE } from 'ol/css';
import { AbstractLayerControl, AbstractLayerEntry } from './abstract_layer_control';

import { TimeSliderWidget } from './time_slider_widget';

import { WMSLayer } from '../wms_layer';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { icon } from '@fortawesome/fontawesome-svg-core'

import { MAP_TIME, TIME_RANGE, LINEAR, DISCRETE, DiscreteRange } from '../time/time';

import { formatISO, clamp as clampTime, compareAsc  } from 'date-fns';

import { scaleTime } from 'd3-scale';

import './time_slider.css';

const SLIDER_HEIGHT = 40;

class TimeEntry extends AbstractLayerEntry {
	constructor( layer, parent ) {
		super(layer);
		this.control = parent;
				
		this.listen(this.layer.on("propertychange", ({ key }) => {
			if( key === TIME_RANGE ) {
				this.updateEntry();
			}
		}));
		
		this.listen(this.layer.on("change:visible", () => this.updateEntry()));
	}
	
	getRange() {
		if( this.layer.getVisible() ) {			
			return this.layer.get(TIME_RANGE);
		} 
	}
	
	updateEntry() {
		this.control.refreshRange();
	}
	
	remove() {
		super.remove();
	}
	
	updateTime( nextTime ) {
		if( this.instance instanceof WMSLayer ) {
			this.instance.updateTime(nextTime);		
		}	
	}
}

class TimeSlider extends AbstractLayerControl {

	constructor(opt_options) {
	    const options = opt_options ? opt_options : {};
	
		super({
      		element: document.createElement('div'),
      		render: options.render,
    	});
	
	   	this.dragListenerKeys_ = [];
    	this.dragging_;
    	this.heightLimit_ = 0;
    	this.widthLimit_ = 0;
    	this.startX_;
    	this.startY_;
    	this.duration_ = options.duration !== undefined ? options.duration : 200;
    	
    	this.map_width = 0;
    	
    	const className = options.className !== undefined ? options.className : 'ol-timeslider';

		const createButton = (styleName, buttonIcon) => {
			const buttonElement = document.createElement('button');
    		buttonElement.setAttribute('type', 'button');
	    	buttonElement.className = `${className}-${styleName} ${CLASS_UNSELECTABLE}`;
	    	
	    	const iconNode = icon(buttonIcon).node[0]
    		buttonElement.appendChild( iconNode );
    		
    		buttonElement.style.visibility = 'hidden';
    		
    		return buttonElement;
		}

		const previousElement = createButton('previous', faChevronLeft);
		const nextElement = createButton('next', faChevronRight);

		previousElement.addEventListener(EventType.CLICK, this.handlePreviousClick.bind(this), false);
		nextElement.addEventListener(EventType.CLICK, this.handleNextClick.bind(this), false);

		this.previousElement = previousElement;
		this.nextElement = nextElement;

		const imageElement = document.createElement("div");
		imageElement.className = `${className}-inner`;

		const containerElement = this.element;
		containerElement.className = `${className} ${CLASS_UNSELECTABLE} ${CLASS_CONTROL}`;
		containerElement.appendChild(previousElement);
		containerElement.appendChild(imageElement);
		containerElement.appendChild(nextElement);
		
    	this.widget = new TimeSliderWidget({
    		width: 100,
    		height: SLIDER_HEIGHT,
    		getMap: () => this.getMap()
    	});	    		

		
		imageElement.append( this.widget.node() );    			    		
    			    			
    	this.values = undefined;
    	this.element.style.display = 'none';
	}
		   	
	createEntry( layer ) {
		 return new TimeEntry(layer, this);
	}
	
	acceptLayer(layer) {
		return layer instanceof WMSLayer;
	}
   	
   	setMap(map) {
   		super.setMap(map);
   		
   		if( map ) {   		
   			// TODO unlisten
   			map.on('propertychange', ({key}) => {
   				if( key === MAP_TIME ) {
   					this.widget.updateMapTime();
   					this.updateTime();
   				}
   			}); 		
   		}   		
   	}
   	
   	updateValues( displayType, values ) {
   		this.displayType = displayType;
   		this.values = undefined;
   		this.timeScale = undefined;
   		
   		if( values && values.length > 1 )  {
   			const range = [];
   			for( let i = 0; i < values.length; i++) {
   				range.push( i );
   			}
   		
   			this.values = scaleTime()
   			.domain(values)
   			.range(range)
   			.clamp(true);
   			   			
   			this.timeScale = scaleTime()
			.domain([values[0], values.at(-1)])
			.range([0, 1]);
   			
   			this.checkMapTime();
   		}   		
   		
   		this.widget.updateValues( displayType, this.values, this.timeScale );  
   	}
   	
   	checkMapTime() {   	
		// fix appropriate map time
		const time = this.getMap().get(MAP_TIME) || new Date();
		
		let indexValue = this.values(time);
					
		if( this.displayType === DISCRETE ) {
			indexValue = Math.round(indexValue); 
		}
		
		const clampedTime = this.values.invert(indexValue); 
		this.getMap().set(MAP_TIME, clampedTime);
   	}
   	
   	updateTime() {
		const time = this.getMap().get(MAP_TIME);
		let timeString = undefined;
		
		if( !!time ) {
			timeString = formatISO(time);
		}
		
		this.getLayers()
		.forEach(layer => layer.updateTime(timeString));
	}
   	
   	refresh() {
   		this.refreshRange();
   	} 
   	
   	refreshRange() {
		let ranges = this.getLayers()
		.map(entry => entry.getRange())
		.filter(range => !!range);
																
		let currentTime = this.getMap().get(MAP_TIME);
		let displayType = LINEAR;
		let valueRange = undefined;
								
		if( ranges.length > 0 ) {
			const valueSet = new Set();
			ranges.forEach( range => range.values.forEach( value => valueSet.add(value) ) );
				
			valueRange = [...valueSet.values()].sort(compareAsc);
			
			if( ranges.every( value => value instanceof DiscreteRange ) ) {
				displayType = DISCRETE;
			}								
		} else {
			currentTime = undefined;	
		}
		
		this.getMap().set(MAP_TIME, currentTime);
		this.updateValues(displayType, valueRange);

		this.element.style.display = this.values === undefined ? "none" : "";	
	}
   	
   	handlePreviousClick() {   	
   		this.handleRelativePosition(-1);
  	}
  	
  	handleNextClick() {   		
    	this.handleRelativePosition(1);
  	}
  	
  	handleRelativePosition(offset) {
  	   	const mapTime = this.getMap().get(MAP_TIME);
   		
   		if( mapTime && this.values ) { 
   			const index = this.values(mapTime) + offset;
   		
   			const dateTime = this.values.invert(index);
   			this.getMap().set(MAP_TIME, dateTime);	   		
   		}  	
  	}
  	  	  	
  	render(mapEvent) {
  		if (!mapEvent.frameState) {
      		return;
    	}    	
    	
    	if( this.values ) {    	
	    	const { map } = mapEvent;
    		const size = map.getSize();
    	
    		if( this.map_width !== size[0] ) {
    			this.widget.updateSize();
    			this.map_width = size[0];
    		}
    	   	    	  	
    		this.updateButtons();    	
    		this.widget.updateMapTime();
    	}    	    	
	}
	
	updateButtons() {
		if( this.getMap() && this.timeScale && this.displayType === DISCRETE ) {
			const mapTime = this.getMap().get(MAP_TIME);
			    	
    		const position = this.timeScale(mapTime);
    		
    		if( position < 1 ) {
    			this.nextElement.style.visibility = '';    		
    		} else {
    			this.nextElement.style.visibility = 'hidden';	
    		}

			if( position > 0 ) {
    			this.previousElement.style.visibility = '';    		
    		} else {
    			this.previousElement.style.visibility = 'hidden';	
    		} 
		} else {
			this.nextElement.style.visibility = 'hidden';	
		    this.previousElement.style.visibility = 'hidden';			
		}
	}	
	
}

export { TimeSlider }